<template>
  <div class="form__field">
    <div class="user-image" v-for="(image, i) in fileList" :key="i">
      <img :src="previews[i]" />
      <input
        type="text"
        class="js-dropzone-caption"
        :name="`captions[${i}]`"
        placeholder="Add a caption (optional)"
      />
    </div>
    <div
      class="submit-images-main"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
      ref="dropzone"
    >
      <div v-if="isLoading">
        <h3>Loading Images...</h3>
      </div>
      <div v-else>
        <input
          id="images[]"
          name="images[]"
          type="file"
          multiple
          accept=".jpg,.jpeg,.png"
          ref="file"
          @change="handleChange"
        />
        <h3>Drag and drop photos here</h3>
        <p>
          or
          <span class="button button--override">Choose Files</span>
          <span
            v-if="this.fileList.length > 0"
            class="button button--remove"
            @click="clearFileList"
          >
            Remove All Images
          </span>
        </p>
      </div>
    </div>
    <span class="error" v-if="error">{{ error }}</span>
    <span class="description" v-else>Max Image Size: 10MB</span>
    <span class="error" v-if="serverError.length && showServerError">
      {{ serverError }}
    </span>
  </div>
</template>

<script>
  import heic2any from 'heic2any'

  export default {
    name: 'FormDropzone',

    data() {
      return {
        fileList: [],
        previews: [],
        isLoading: false,
        error: null,
        showServerError: true,
      }
    },

    props: {
      serverError: String,
    },

    methods: {
      dragover(e) {
        e.preventDefault()
        // Add some visual fluff to show the user can drop its files
        if (!this.$refs.dropzone.classList.contains('bg-green-300')) {
          this.$refs.dropzone.classList.remove('bg-gray-100')
          this.$refs.dropzone.classList.add('bg-green-300')
        }
      },

      dragleave(e) {
        // Clean up
        this.$refs.dropzone.classList.add('bg-gray-100')
        this.$refs.dropzone.classList.remove('bg-green-300')
      },

      drop(e) {
        e.preventDefault()

        this.$refs.file.files = e.dataTransfer.files

        this.handleChange() // Trigger the onChange event manually

        // Clean up
        this.$refs.dropzone.classList.add('bg-gray-100')
        this.$refs.dropzone.classList.remove('bg-green-300')
      },

      async getPreviewImage(file) {
        // console.log('running')
        const img = file.type.includes('heic')
          ? await this.convertHeicToJpeg(file)
          : file

        // console.log(img)

        // console.log(URL.createObjectURL(img))
        return await URL.createObjectURL(img)
      },

      async handleChange() {
        const { files } = this.$refs.file

        if (!files.length) return false

        const imagesAreValid = this.validateImageSizes(files)

        if (imagesAreValid) {
          this.isLoading = !this.isLoading
          this.error = null
          this.showServerError = false

          const fileNames = await this.uploadTempImages(files)

          if (!fileNames) {
              this.error = 'Hey there! We love that you have so many great images, but just try to pick the best 2 or 3. We have to limit you to 10MB total!'
              this.isLoading = !this.isLoading

              return false
          }

          const newInputs = await this.createNewInputs(fileNames)

          await this.appendInputFragment(newInputs)

          this.fileList = [...this.fileList, ...files]

          this.previews = await Promise.all(
            this.fileList.map(async file => await this.getPreviewImage(file))
          )

          this.isLoading = !this.isLoading
        }
      },

      validateImageSizes(files) {
        const arr = Array.from(files)
        let isValid = true

        for (let file of arr) {
          // only show images less than 10mb -- 10000000 kb
          if (file.size > 10000000) {
            this.error =
              arr.length > 1
                ? 'One or more of your images was larger than 10MB.'
                : 'Please upload an image smaller than 10MB.'

            isValid = false

            break
          } // 10mb
        }

        return isValid
      },

      async uploadTempImages(files) {
        const formData = new FormData()

        for (let file of Array.from(files)) {
          const img = file.type.includes('heic')
            ? await this.convertHeicToJpeg(file)
            : file
          formData.append('temp_images[]', img, img.name)
        }

        try {
          const { data } = await axios.post('/dropzone', formData)

          return data.file_names
        } catch (error) {
          console.log(error)
          return false
        }
      },

      convertHeicToJpeg(file) {
        return new Promise(resolve => {
          resolve(
            heic2any({
              blob: file,
              toType: 'image/jpeg',
              quality: 0.8,
            })
          )
        })
      },

      createNewInputs(fileNames) {
        return Promise.all(
          fileNames.map((fileName, i) => {
            let index = this.fileList.length

            const newIndex = i === 0 && index === 0 ? 0 : index + i
            return this.createFileNameInput(fileName, newIndex)
          })
        )
      },

      createFileNameInput(fileName, i) {
        const input = document.createElement('input')
        input.classList.add('js-hidden-filenames')
        input.name = `file_names[${i}]`
        input.type = 'text'
        input.style.display = 'none'
        input.value = fileName // set input value equal to the file

        return input
      },

      async appendInputFragment(array) {
        const fragment = document.createDocumentFragment()

        for (let input of array) fragment.appendChild(input)

        await this.$el.append(fragment)
      },

      clearFileList() {
        this.$refs.file.value = ''
        this.error = null
        this.fileList = []
        this.previews = []

        const $hiddenFiles = document.querySelectorAll('.js-hidden-filenames')

        $hiddenFiles.forEach(el => el.remove())

        this.handleChange()
        this.isLoading = false
      },
    },
  }
</script>

<style>
  .submit-images-main .button--remove {
    font-family: 'Bureau Grot Bk', helvetica;
    font-weight: 400;
    font-size: 12px;
    letter-spacing: 0.3px;
    line-height: 1;
    text-transform: uppercase;
    margin: 0 12px 0 6px;
    vertical-align: middle;
    color: #000 !important;
    background-color: #eae5db !important;
  }
</style>
